import React, { useState, useMemo, useCallback, useEffect } from 'react';
import ExpandMoreIcon         from '@mui/icons-material/ExpandMore';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon             from '@mui/icons-material/Person';
import WhatshotIcon           from '@mui/icons-material/Whatshot';
import AccountTreeIcon        from '@mui/icons-material/AccountTree';
import FiberManualRecordIcon  from '@mui/icons-material/FiberManualRecord';
import CloseIcon              from '@mui/icons-material/Close';

import SimulationCard from '../../atoms/SimulationCard/SimulationCard';
import MyHistoryPanel from '../MyHistoryPanel/MyHistoryPanel';
import ViewSwitcher   from '../../atoms/ViewSwitcher/ViewSwitcher';
import DateRangePicker from '../../molecules/DateRangePicker/DateRangePicker';
import './HierarchyExplorer.css';

const MODES = [
  { key: 'my_history', label: 'Mon historique' },
  { key: 'team',       label: "Historique d'équipe" },
];

const DEFAULT_FILTERS = { reference: '', dateFrom: '', dateTo: '' };

// ─────────────────────────────────────────────────────────────────────────────
// buildTree — reconstruit l'arbre récursif depuis la liste plate
// ─────────────────────────────────────────────────────────────────────────────
const buildTree = (adminId, allUsers, visited = new Set()) => {
  if (visited.has(String(adminId))) return { own_users: [], sub_admins: [] };
  visited.add(String(adminId));

  const directChildren = allUsers.filter((u) =>
    (u.supervisor_ids || []).map(String).includes(String(adminId))
  );

  const own_users = directChildren.filter((u) => u.role === 'user');
  const subAdmins = directChildren.filter((u) => u.role === 'admin');

  const sub_admins = subAdmins.map((admin) => {
    const branch = buildTree(admin.id, allUsers, new Set(visited));
    const total  =
      branch.own_users.length +
      branch.sub_admins.reduce(
        (s, n) => s + (n.total_users_in_branch ?? 0) + (n.own_users?.length ?? 0), 0
      );
    return { admin, own_users: branch.own_users, sub_admins: branch.sub_admins, total_users_in_branch: total };
  });

  return { own_users, sub_admins };
};

// ─────────────────────────────────────────────────────────────────────────────
// buildSuperadminTree — pour le superadmin : tous les admins racines + leurs arbres
// Un admin "racine" = aucun autre admin dans supervisor_ids
// ─────────────────────────────────────────────────────────────────────────────
const buildSuperadminTree = (allUsers) => {
  const adminIds = new Set(allUsers.filter(u => u.role === 'admin').map(u => String(u.id)));

  // Admins racines = admins dont aucun supervisor_ids n'est lui-même un admin
  const rootAdmins = allUsers.filter((u) => {
    if (u.role !== 'admin') return false;
    const supervisorAdminIds = (u.supervisor_ids || []).filter(id => adminIds.has(String(id)));
    return supervisorAdminIds.length === 0;
  });

  // Users sans aucun supervisor admin
  const orphanUsers = allUsers.filter((u) => {
    if (u.role !== 'user') return false;
    const hasSupervisor = (u.supervisor_ids || []).some(id => adminIds.has(String(id)));
    return !hasSupervisor;
  });

  const sub_admins = rootAdmins.map((admin) => {
    const branch = buildTree(admin.id, allUsers, new Set());
    const total  =
      branch.own_users.length +
      branch.sub_admins.reduce(
        (s, n) => s + (n.total_users_in_branch ?? 0) + (n.own_users?.length ?? 0), 0
      );
    return { admin, own_users: branch.own_users, sub_admins: branch.sub_admins, total_users_in_branch: total };
  });

  return { own_users: orphanUsers, sub_admins };
};

// ─────────────────────────────────────────────────────────────────────────────
// Tri des utilisateurs par simulation la plus récente
// ─────────────────────────────────────────────────────────────────────────────
const getLatestSimTime = (userId, simsByUser) => {
  const sims = simsByUser[userId]?.sims;
  if (!sims || !sims.length) return null;
  let latest = null;
  for (const s of sims) {
    const raw = s?.fire?.created_at;
    if (!raw) continue;
    const t = new Date(raw).getTime();
    if (!Number.isNaN(t) && (latest === null || t > latest)) latest = t;
  }
  return latest;
};

const sortUsersByLatestSim = (users, simsByUser) => {
  // Tri stable : ceux qui ont une date connue passent devant (plus récent → plus ancien).
  // Ceux sans données chargées gardent leur position relative d'origine, en fin de liste.
  return [...users].sort((a, b) => {
    const ta = getLatestSimTime(a.id, simsByUser);
    const tb = getLatestSimTime(b.id, simsByUser);
    if (ta !== null && tb !== null) return tb - ta;
    if (ta !== null && tb === null) return -1;
    if (ta === null && tb !== null) return 1;
    return 0;
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// flattenTree — aplati tout l'arbre (admins + users, à toute profondeur)
// pour permettre une recherche globale, peu importe le niveau hiérarchique.
// ─────────────────────────────────────────────────────────────────────────────
const flattenTree = (tree) => {
  const admins = [];
  const users  = [...tree.own_users];

  const walk = (node) => {
    admins.push(node);
    users.push(...node.own_users);
    node.sub_admins.forEach(walk);
  };

  tree.sub_admins.forEach(walk);

  return { admins, users };
};

// ─────────────────────────────────────────────────────────────────────────────
// pruneTreeByMatch — élague récursivement : ne garde que les admins/users qui
// ont eux-mêmes une simulation correspondant au filtre, ou qui ont au moins
// un descendant qui matche.
// ─────────────────────────────────────────────────────────────────────────────
const pruneTreeByMatch = (tree, hasMatch) => {
  const pruneAdmin = (node) => {
    const prunedSub    = node.sub_admins.map(pruneAdmin).filter(Boolean);
    const matchingOwn  = node.own_users.filter((u) => hasMatch(u.id));
    const adminMatches = hasMatch(node.admin.id);

    if (!adminMatches && matchingOwn.length === 0 && prunedSub.length === 0) return null;

    return {
      ...node,
      own_users: matchingOwn,
      sub_admins: prunedSub,
      // conservé pour info même si vide, au cas où on veut l'afficher plus tard
    };
  };

  return {
    own_users: tree.own_users.filter((u) => hasMatch(u.id)),
    sub_admins: tree.sub_admins.map(pruneAdmin).filter(Boolean),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Conversion string ISO (YYYY-MM-DD, format backend) ↔ objet Date local
// (évite les décalages de fuseau horaire d'un new Date('YYYY-MM-DD') classique,
// qui est interprété en UTC et peut décaler d'un jour selon le fuseau du poste).
// ─────────────────────────────────────────────────────────────────────────────
const parseISODate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const formatISODate = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// FiltersBar — filtres globaux "référence" + "plage de dates", envoyés au
// backend (fires_by_user / get_myfires). Distincte de la barre de recherche
// admin/user. Composant générique, réutilisé pour "Mon historique" et
// "Historique d'équipe" avec des filtres/callbacks différents à chaque fois.
// ─────────────────────────────────────────────────────────────────────────────
const FiltersBar = ({ filters, onFiltersChange, onFiltersReset }) => {
  const [inputValue, setInputValue] = useState(filters.reference);

  useEffect(() => {
    setInputValue(filters.reference);
  }, [filters.reference]);

  useEffect(() => {
    const t = setTimeout(() => {
      onFiltersChange({ reference: inputValue });
    }, 300);
    return () => clearTimeout(t);
  }, [inputValue, onFiltersChange]);

  const hasActiveFilters = !!(filters.reference || filters.dateFrom || filters.dateTo);

  const startDate = parseISODate(filters.dateFrom);
  const endDate   = parseISODate(filters.dateTo);

  return (
    <div className="he-filters-bar">
      <div className="he-filters-group">
        <span className="he-filters-label">Filtrer par réf. de simulation</span>
        <input
          className="he-filters-input"
          type="text"
          placeholder="Référence de simulation"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
      </div>

      <div className="he-filters-group">
        <span className="he-filters-label">Filtrer par date</span>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartChange={(date) => onFiltersChange({ dateFrom: formatISODate(date) })}
          onEndChange={(date) => onFiltersChange({ dateTo: formatISODate(date) })}
        />
      </div>

      {hasActiveFilters && (
        <button
          type="button"
          className="he-filters-clear"
          onClick={() => {
            setInputValue('');
            onFiltersReset();
          }}
          title="Réinitialiser les filtres"
        >
          <CloseIcon style={{ fontSize: 12 }} />
          Réinitialiser
        </button>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// UserNode
// ─────────────────────────────────────────────────────────────────────────────
const UserNode = ({
  user,
  simsByUser,
  selectedSimId,
  onLoadSims,
  onSelectSim,
  depth = 0,
  autoExpand = false,
  filtersActive = false,
}) => {
  const [open, setOpen] = useState(false);

  const entry   = simsByUser[user.id];
  const sims    = entry?.sims    ?? [];
  const loading = entry?.loading ?? false;

  // Fetch initial au clic, et re-fetch automatique si le cache est invalidé
  // (changement de filtres) pendant que le panneau reste ouvert.
  // Coupé quand un filtre équipe est actif : c'est `onLoadTeamSimsBulk` qui
  // s'occupe déjà de recharger `simsByUser` en un seul appel réseau. Sans
  // cette garde, chaque nœud auto-ouvert par un filtre relance son propre
  // fetch individuel (fires_by_user) en parallèle du bulk, à chaque
  // changement de filtre (le cache simsByUser est vidé à chaque changement).
  useEffect(() => {
    if (filtersActive) return;
    if (open && !entry) onLoadSims(user.id);
  }, [open, entry, onLoadSims, user.id, filtersActive]);

  // Ouverture automatique quand ce nœud fait partie des résultats filtrés
  // (évite d'avoir à recliquer sur chaque utilisateur correspondant).
  useEffect(() => {
    if (autoExpand) setOpen(true);
  }, [autoExpand]);

  const handleToggle = useCallback(() => {
    setOpen((v) => !v);
  }, []);

  const displayName = user.name
    ? `${user.name} ${user.lastname ?? ''}`.trim()
    : user.username;

  return (
    <div className="he-user-node" style={{ '--depth': depth }}>
      <button
        className={`he-user-node__btn ${open ? 'he-user-node__btn--open' : ''}`}
        onClick={handleToggle}
      >
        <div className={`he-user-node__avatar ${user.is_online ? 'he-user-node__avatar--online' : ''}`}>
          {(user.name?.[0] || user.username?.[0] || 'U').toUpperCase()}
          {user.is_online && <span className="he-user-node__dot" />}
        </div>
        <div className="he-user-node__info">
          <span className="he-user-node__name">{displayName}</span>
          <span className="he-user-node__role">
            <PersonIcon style={{ fontSize: 9 }} /> utilisateur
          </span>
        </div>
        <div className="he-user-node__meta">
          {entry && sims.length > 0 && (
            <span className="he-user-node__badge">🔥 {sims.length}</span>
          )}
          <ExpandMoreIcon
            className={`he-user-node__arrow ${open ? 'he-user-node__arrow--open' : ''}`}
          />
        </div>
      </button>

      {open && (
        <div className="he-user-node__panel">
          {loading ? (
            Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="he-skeleton" style={{ height: 56, animationDelay: `${i * 0.1}s` }} />
            ))
          ) : sims.length === 0 ? (
            <div className="he-node-empty">Aucune simulation</div>
          ) : (
            sims.map((sim, i) => (
              <SimulationCard
                key={sim.fire?.id ?? i}
                simulation={sim}
                isSelected={selectedSimId === sim.fire?.id}
                onClick={onSelectSim}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// AdminNode — récursif
// ─────────────────────────────────────────────────────────────────────────────
const AdminNode = ({
  adminInfo,
  own_users,
  sub_admins,
  simsByUser,
  selectedSimId,
  onLoadSims,
  onSelectSim,
  depth  = 0,
  isRoot = false,
  autoExpand = false,
  filtersActive = false,
}) => {
  const [simsOpen,   setSimsOpen]   = useState(false);
  const [usersOpen,  setUsersOpen]  = useState(false);
  const [subAdmOpen, setSubAdmOpen] = useState(false);

  const adminId      = adminInfo?.id;
  const entry        = simsByUser[adminId];
  const adminSims    = entry?.sims    ?? [];
  const adminLoading = entry?.loading ?? false;

  const displayName = adminInfo
    ? (adminInfo.name
        ? `${adminInfo.name} ${adminInfo.lastname ?? ''}`.trim()
        : adminInfo.username)
    : 'Mon équipe';

  // Fetch initial au clic, et re-fetch automatique si le cache est invalidé
  // (changement de filtres) pendant que le panneau reste ouvert.
  // Coupé quand un filtre équipe est actif (cf. commentaire détaillé dans
  // UserNode) : le bulk préchargement s'en charge déjà.
  useEffect(() => {
    if (filtersActive) return;
    if (simsOpen && !entry && adminId) onLoadSims(adminId);
  }, [simsOpen, entry, adminId, onLoadSims, filtersActive]);

  // Ouverture automatique quand ce nœud fait partie des résultats filtrés
  // (l'arbre a déjà été élagué en amont : tout ce qui arrive ici matche).
  useEffect(() => {
    if (!autoExpand) return;
    setSimsOpen(true);
    if (own_users.length > 0)  setUsersOpen(true);
    if (sub_admins.length > 0) setSubAdmOpen(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoExpand]);

  const handleToggleSims = useCallback(() => {
    setSimsOpen((v) => !v);
  }, []);

  // ── Préchargement silencieux des simulations des users directs ────────────
  // Dès l'ouverture du panneau "Utilisateurs directs", on charge en arrière-plan
  // les simulations de chacun (si pas déjà en cache) afin de pouvoir les trier
  // par récence, sans attendre que l'utilisateur ouvre chaque carte une à une.
  // Redéclenché aussi quand simsByUser change (ex: cache vidé par un filtre).
  // Coupé quand un filtre équipe est actif : `simsByUser` est vidé à CHAQUE
  // changement de filtre (cf. useSimulations), donc sans cette garde, ce
  // effect reboucle sur tous les own_users à chaque frappe/suppression dans
  // la barre de recherche, en plus du bulk préchargement — c'était la cause
  // du volume de logs constaté en supprimant le texte du filtre référence.
  useEffect(() => {
    if (!usersOpen || own_users.length <= 1) return;
    if (filtersActive) return;
    own_users.forEach((u) => {
      if (!simsByUser[u.id]) onLoadSims(u.id);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersOpen, simsByUser, filtersActive]);

  const sortedOwnUsers = useMemo(
    () => (own_users.length > 1 ? sortUsersByLatestSim(own_users, simsByUser) : own_users),
    [own_users, simsByUser]
  );

  // Une seule couleur pour tous les niveaux
  const accent = '#2d3748';

  return (
    <div
      className={`he-admin-node ${isRoot ? 'he-admin-node--root' : ''}`}
      style={{ '--depth': depth, '--accent': accent }}
    >
      {/* Header — masqué pour la racine */}
      {!isRoot && (
        <div className="he-admin-node__head">
          <div
            className={`he-admin-node__avatar ${adminInfo?.is_online ? 'he-admin-node__avatar--online' : ''}`}
            style={{ background: accent }}
          >
            {(adminInfo?.name?.[0] || adminInfo?.username?.[0] || 'A').toUpperCase()}
            {adminInfo?.is_online && <span className="he-admin-node__dot" />}
          </div>
          <div className="he-admin-node__info">
            <span className="he-admin-node__name">{displayName}</span>
            <span className="he-admin-node__role">
              <AdminPanelSettingsIcon style={{ fontSize: 9 }} />
              {adminInfo?.role ?? 'admin'}
            </span>
          </div>
          <div className="he-admin-node__badges">
            {own_users.length > 0 && (
              <span className="he-admin-node__badge he-admin-node__badge--users">
                <PersonIcon style={{ fontSize: 9 }} />
                {own_users.length}
              </span>
            )}
            {sub_admins.length > 0 && (
              <span className="he-admin-node__badge he-admin-node__badge--sub">
                <AccountTreeIcon style={{ fontSize: 9 }} />
                {sub_admins.length}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="he-admin-node__actions">

        {/* Simulations (pas pour la racine) */}
        {!isRoot && (
          <>
            <button
              className={`he-admin-node__action-btn he-admin-node__action-btn--fire ${simsOpen ? 'he-admin-node__action-btn--open' : ''}`}
              onClick={handleToggleSims}
            >
              <WhatshotIcon style={{ fontSize: 12 }} />
              <span>Ses simulations</span>
              {entry && adminSims.length > 0 && (
                <span className="he-admin-node__action-count">{adminSims.length}</span>
              )}
              <ExpandMoreIcon className={`he-admin-node__arrow ${simsOpen ? 'he-admin-node__arrow--open' : ''}`} />
            </button>

            {simsOpen && (
              <div className="he-admin-node__panel">
                {adminLoading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="he-skeleton" style={{ height: 64, animationDelay: `${i * 0.1}s` }} />
                  ))
                ) : adminSims.length === 0 ? (
                  <div className="he-node-empty">Aucune simulation</div>
                ) : (
                  adminSims.map((sim, i) => (
                    <SimulationCard
                      key={sim.fire?.id ?? i}
                      simulation={sim}
                      isSelected={selectedSimId === sim.fire?.id}
                      onClick={onSelectSim}
                    />
                  ))
                )}
              </div>
            )}
          </>
        )}

        {/* Utilisateurs directs — triés par simulation la plus récente */}
        {own_users.length > 0 && (
          <>
            <button
              className={`he-admin-node__action-btn he-admin-node__action-btn--users ${usersOpen ? 'he-admin-node__action-btn--open' : ''}`}
              onClick={() => setUsersOpen((v) => !v)}
            >
              <PersonIcon style={{ fontSize: 12 }} />
              <span>Utilisateurs directs</span>
              <span className="he-admin-node__action-count">{own_users.length}</span>
              <ExpandMoreIcon className={`he-admin-node__arrow ${usersOpen ? 'he-admin-node__arrow--open' : ''}`} />
            </button>

            {usersOpen && (
              <div className="he-admin-node__panel he-admin-node__panel--users">
                {sortedOwnUsers.map((u) => (
                  <UserNode
                    key={u.id}
                    user={u}
                    simsByUser={simsByUser}
                    selectedSimId={selectedSimId}
                    onLoadSims={onLoadSims}
                    onSelectSim={onSelectSim}
                    depth={depth + 1}
                    autoExpand={autoExpand}
                    filtersActive={filtersActive}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Sous-admins récursifs */}
        {sub_admins.length > 0 && (
          <>
            <button
              className={`he-admin-node__action-btn he-admin-node__action-btn--sub ${subAdmOpen ? 'he-admin-node__action-btn--open' : ''}`}
              onClick={() => setSubAdmOpen((v) => !v)}
            >
              <AccountTreeIcon style={{ fontSize: 12 }} />
              <span>Sous-admins</span>
              <span className="he-admin-node__action-count">{sub_admins.length}</span>
              <ExpandMoreIcon className={`he-admin-node__arrow ${subAdmOpen ? 'he-admin-node__arrow--open' : ''}`} />
            </button>

            {subAdmOpen && (
              <div className="he-admin-node__panel he-admin-node__panel--sub">
                {sub_admins.map((node) => (
                  <AdminNode
                    key={node.admin.id}
                    adminInfo={node.admin}
                    own_users={node.own_users}
                    sub_admins={node.sub_admins}
                    simsByUser={simsByUser}
                    selectedSimId={selectedSimId}
                    onLoadSims={onLoadSims}
                    onSelectSim={onSelectSim}
                    depth={depth + 1}
                    autoExpand={autoExpand}
                    filtersActive={filtersActive}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// HierarchyExplorer — composant principal
// ════════════════════════════════════════════════════════════════════════════
const HierarchyExplorer = ({
  admins        = [],
  users         = [],
  loadingAdmins = false,
  selectedSimId,
  expandedUserId,
  onExpandUser,
  onSelectSim,
  currentUserId,
  currentUserRole,   // ← 'admin' | 'superadmin'
  selectedAdminId,
  onSelectAdmin,

  // ── Équipe ──
  simsByUser         = {},
  onLoadSims,
  onLoadTeamSimsBulk, // ← préchargement bulk pour l'élagage par filtre
  teamFilters        = DEFAULT_FILTERS,
  onTeamFiltersChange = () => {},
  onTeamFiltersReset  = () => {},

  // ── Mon historique ──
  mySimsByUser        = {},
  onLoadMyFires,
  myFilters            = DEFAULT_FILTERS,
  onMyFiltersChange     = () => {},
  onMyFiltersReset      = () => {},
}) => {
  const [mode,   setMode]   = useState('my_history');
  const [search, setSearch] = useState('');

  // ── Chargement initial de "Mon historique" ─────────────────────────────
  // currentUserId arrive souvent en async depuis un parent (le profil de
  // l'utilisateur connecté se charge après le premier rendu). Si le fetch
  // initial dépendait d'un effet interne à MyHistoryPanel monté une seule
  // fois avec un tableau de deps vide, il capturait currentUserId=undefined
  // et ne relançait plus jamais — d'où la liste vide tant qu'on ne quitte
  // pas puis revient sur "Mon historique" (ce qui remonte le composant et
  // relance l'effet, cette fois avec le bon id). Ici on redéclenche le fetch
  // au niveau du parent dès que currentUserId devient disponible, tant que
  // rien n'est encore en cache pour lui — indépendant du cycle de vie de
  // MyHistoryPanel.
  useEffect(() => {
    if (!currentUserId) return;
    if (mySimsByUser[currentUserId]) return; // déjà chargé ou en cours
    onLoadMyFires?.(currentUserId);
  }, [currentUserId, mySimsByUser, onLoadMyFires]);

  const isSuperAdmin = currentUserRole === 'superadmin';
  const hasActiveFilters = !!(teamFilters.reference || teamFilters.dateFrom || teamFilters.dateTo);

  // ── Fusion users + admins sans doublons ───────────────────────────────────
  const allUsers = useMemo(() => {
    const map = new Map();
    [...users, ...admins].forEach((u) => map.set(String(u.id), u));
    return Array.from(map.values());
  }, [users, admins]);

  // ── Construction de l'arbre ───────────────────────────────────────────────
  const tree = useMemo(() => {
    if (isSuperAdmin) {
      return buildSuperadminTree(allUsers);
    }
    if (!currentUserId) return { own_users: [], sub_admins: [] };
    return buildTree(currentUserId, allUsers);
  }, [currentUserId, isSuperAdmin, allUsers]);

  // ── Recherche globale sur TOUT l'arbre (admins + users, toute profondeur) ──
  const { admins: allAdminNodes, users: allTreeUsers } = useMemo(
    () => flattenTree(tree),
    [tree]
  );

  // ── Préchargement pour l'élagage par filtre référence/dates ───────────────
  // Liste dédupliquée de tous les ids (admins + users) présents dans l'arbre.
  const allIdsToPreload = useMemo(() => {
    const map = new Map();
    allAdminNodes.forEach((n) => map.set(String(n.admin.id), n.admin.id));
    allTreeUsers.forEach((u) => map.set(String(u.id), u.id));
    return Array.from(map.values());
  }, [allAdminNodes, allTreeUsers]);

  // Préchargement en un seul appel réseau : `onLoadTeamSimsBulk` tape un
  // endpoint bulk côté backend et hydrate `simsByUser` pour tous les ids en
  // une passe, au lieu de l'ancien N+1 (une requête par admin/user, même
  // étalée avec une limite de concurrence).
  useEffect(() => {
    if (mode !== 'team' || !hasActiveFilters) return;
    const idsToLoad = allIdsToPreload.filter((id) => !simsByUser[id]);
    if (idsToLoad.length === 0) return;
    onLoadTeamSimsBulk?.(idsToLoad);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, hasActiveFilters, allIdsToPreload, simsByUser, onLoadTeamSimsBulk]);

  const isPreloadingForFilter = mode === 'team' && hasActiveFilters &&
    allIdsToPreload.some((id) => !simsByUser[id] || simsByUser[id].loading);

  const hasMatch = useCallback(
    (id) => (simsByUser[id]?.sims?.length ?? 0) > 0,
    [simsByUser]
  );

  // ── Arbre élagué : uniquement les branches ayant une simulation matchant
  // le filtre référence/dates actif. Identique à `tree` si aucun filtre.
  const prunedTree = useMemo(() => {
    if (!hasActiveFilters || isPreloadingForFilter) return tree;
    return pruneTreeByMatch(tree, hasMatch);
  }, [tree, hasActiveFilters, isPreloadingForFilter, hasMatch]);

  const matchesTerm = (entity, q) => {
    return (
      `${entity?.name ?? ''} ${entity?.lastname ?? ''}`.toLowerCase().includes(q) ||
      entity?.username?.toLowerCase().includes(q) ||
      entity?.organization?.toLowerCase().includes(q) ||
      entity?.email?.toLowerCase().includes(q)
    );
  };

  const searchResults = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return null; // pas de recherche active → on affiche l'arbre normal

    // ── Déduplication par id : un admin/user avec plusieurs superviseurs
    // peut apparaître plusieurs fois dans l'arbre aplati (une fois par branche
    // parente) → on ne garde que la 1ère occurrence trouvée pour chaque id,
    // afin d'éviter les clés React dupliquées et les cartes en double à l'écran.
    // Si un filtre référence/dates est actif, on n'affiche en plus que les
    // entités qui matchent aussi ce filtre (une fois le préchargement terminé).
    const applyFilterGate = hasActiveFilters && !isPreloadingForFilter;

    const adminMap = new Map();
    allAdminNodes.forEach((node) => {
      const id = String(node.admin.id);
      if (adminMap.has(id)) return;
      if (!matchesTerm(node.admin, q)) return;
      if (applyFilterGate && !hasMatch(node.admin.id)) return;
      adminMap.set(id, node);
    });

    const userMap = new Map();
    allTreeUsers.forEach((u) => {
      const id = String(u.id);
      if (userMap.has(id)) return;
      if (!matchesTerm(u, q)) return;
      if (applyFilterGate && !hasMatch(u.id)) return;
      userMap.set(id, u);
    });

    return {
      admins: Array.from(adminMap.values()),
      users:  Array.from(userMap.values()),
    };
  }, [search, allAdminNodes, allTreeUsers, hasActiveFilters, isPreloadingForFilter, hasMatch]);

  // Conservé pour compat (1er niveau uniquement, non utilisé en recherche globale)
  const filteredSubAdmins = tree.sub_admins;

  // ── Stats rapides ─────────────────────────────────────────────────────────
  const totalUsers = useMemo(() =>
    tree.own_users.length +
    tree.sub_admins.reduce((s, n) => s + (n.total_users_in_branch ?? 0) + n.own_users.length, 0),
  [tree]);

  const onlineCount = useMemo(() =>
    allUsers.filter((u) => u.is_online).length,
  [allUsers]);

  const handleModeChange = (k) => { setMode(k); setSearch(''); };

  const SwitcherBar = () => (
    <div className="he-switcher-bar">
      <ViewSwitcher modes={MODES} value={mode} onChange={handleModeChange} color="#be1622" />
    </div>
  );

  // ── Mon historique ────────────────────────────────────────────────────────
  if (mode === 'my_history') {
    return (
      <div className="he-root">
        <SwitcherBar />
        <FiltersBar
          filters={myFilters}
          onFiltersChange={onMyFiltersChange}
          onFiltersReset={onMyFiltersReset}
        />
        <div className="he-scroll-zone">
          <MyHistoryPanel
            currentUserId={currentUserId}
            simsByUser={mySimsByUser}
            selectedSimId={selectedSimId}
            onLoadSims={onLoadMyFires}
            onSelectSim={onSelectSim}
          />
        </div>
      </div>
    );
  }

  // ── Historique équipe ─────────────────────────────────────────────────────
  const isEmpty = prunedTree.own_users.length === 0 && prunedTree.sub_admins.length === 0;

  return (
    <div className="he-root">
      <SwitcherBar />
      <FiltersBar
        filters={teamFilters}
        onFiltersChange={onTeamFiltersChange}
        onFiltersReset={onTeamFiltersReset}
      />

      <div className="he-header">
        <div className="he-header-top">
          <span className="he-title">Historique équipe</span>
          <div className="he-stats">
            {/* <span className="he-stat">
              <FiberManualRecordIcon style={{ fontSize: 9, color: '#22c55e' }} />
              {onlineCount} en ligne
            </span> */}
            {/* <span className="he-stat he-stat--dim">
              {totalUsers} utilisateurs · {tree.sub_admins.length} admins
            </span> */}
          </div>
        </div>

        {(tree.sub_admins.length > 0 || isSuperAdmin) && (
          <div className="he-search">
            <svg viewBox="0 0 20 20" fill="none" className="he-search-icon">
              <circle cx="8.5" cy="8.5" r="5" stroke="currentColor" strokeWidth="1.5" />
              <path d="M13 13l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              className="he-search-input"
              type="text"
              placeholder={isSuperAdmin ? 'Rechercher un admin ou un utilisateur…' : 'Rechercher un admin ou un utilisateur…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="he-search-clear" onClick={() => setSearch('')}>✕</button>
            )}
          </div>
        )}
      </div>

      <div className="he-scroll-zone">
        {loadingAdmins ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="he-skeleton" style={{ animationDelay: `${i * 0.1}s` }} />
          ))
        ) : isPreloadingForFilter ? (
          // ── Préchargement bulk des sims de toute l'équipe pour élaguer l'arbre ──
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="he-skeleton" style={{ animationDelay: `${i * 0.1}s` }} />
          ))
        ) : searchResults ? (
          // ── Mode recherche : liste plate, tous niveaux confondus, dédupliquée ──
          searchResults.admins.length === 0 && searchResults.users.length === 0 ? (
            <div className="he-empty">Aucun résultat pour "{search}"</div>
          ) : (
            <>
              {searchResults.admins.map((node) => (
                <AdminNode
                  key={`search-admin-${node.admin.id}`}
                  adminInfo={node.admin}
                  own_users={node.own_users}
                  sub_admins={node.sub_admins}
                  simsByUser={simsByUser}
                  selectedSimId={selectedSimId}
                  onLoadSims={onLoadSims}
                  onSelectSim={onSelectSim}
                  depth={0}
                  autoExpand={hasActiveFilters}
                  filtersActive={hasActiveFilters}
                />
              ))}
              {searchResults.users.map((u) => (
                <UserNode
                  key={`search-user-${u.id}`}
                  user={u}
                  simsByUser={simsByUser}
                  selectedSimId={selectedSimId}
                  onLoadSims={onLoadSims}
                  onSelectSim={onSelectSim}
                  depth={0}
                  autoExpand={hasActiveFilters}
                  filtersActive={hasActiveFilters}
                />
              ))}
            </>
          )
        ) : isEmpty ? (
          <div className="he-empty">
            {hasActiveFilters
              ? 'Aucune simulation ne correspond à ce filtre dans votre équipe'
              : 'Aucun membre dans votre équipe'}
          </div>
        ) : (
          <AdminNode
            adminInfo={null}
            own_users={prunedTree.own_users}
            sub_admins={prunedTree.sub_admins}
            simsByUser={simsByUser}
            selectedSimId={selectedSimId}
            onLoadSims={onLoadSims}
            onSelectSim={onSelectSim}
            depth={0}
            isRoot
            autoExpand={hasActiveFilters}
            filtersActive={hasActiveFilters}
          />
        )}
      </div>
    </div>
  );
};

export default HierarchyExplorer;